// Mobil bildirim yaşam döngüsü (9.26.1 / 9.26.6).
//
// Bu katman kendiliğinden izin İSTEMEZ. Kullanıcı daha önce izin vermişse mevcut
// Android'de native FCM, iOS'ta Expo token'ını sunucuyla eşler; izin zamanlaması
// ayrı bir ürün kararıdır. Buna rağmen token yenilenmesi, ön planda sayaç
// tazeleme ve bildirime dokunarak ilgili konuşmaya gitme şimdiden tek yerde yaşar.
import { Platform } from 'react-native';
import Constants from 'expo-constants';
import * as Notifications from 'expo-notifications';
import { api } from '../services/api';
import { storage } from '../services/storage';
import { getDeviceLanguage, getDeviceTimeZone, normalizeDisplayLanguage } from '../utils/locale';
import type { NotificationEventType } from '../services/api';
import { systemCallingAvailable } from '../calls/systemCall';
import { translate } from '../i18n/translate';

export type MessageNotificationTarget = {
  type: 'message';
  senderId: number;
};

export type EventNotificationTarget =
  | {
      type: 'notification_event';
      eventId: string;
      eventType: NotificationEventType;
      destination: 'conversation';
      actorId: number;
      entityId?: number;
    }
  | {
      type: 'notification_event';
      eventId: string;
      eventType: NotificationEventType;
      destination: 'reservation';
      actorId: number;
      entityId: number;
    }
  | {
      type: 'notification_event';
      eventId: string;
      eventType: NotificationEventType;
      destination: 'wallet';
    };

const NOTIFICATION_EVENT_TYPES: ReadonlySet<string> = new Set([
  'meetup_requested',
  'meetup_accepted',
  'meetup_declined',
  'meetup_cancelled',
  'meetup_reschedule_requested',
  'meetup_reschedule_accepted',
  'meetup_reschedule_declined',
  'meetup_reschedule_cancelled',
  'call_reservation_requested',
  'call_reservation_accepted',
  'call_reservation_declined',
  'call_reservation_cancelled',
  'call_reservation_reschedule_requested',
  'call_reservation_reschedule_accepted',
  'call_reservation_reschedule_declined',
  'call_reservation_reschedule_cancelled',
  'meetup_reminder',
  'call_reservation_reminder',
  'meetup_starting',
  'call_reservation_starting',
  'call_incoming',
  'call_missed',
  'escrow_expired_refunded',
]);

export type ForegroundMessageNotification = {
  senderId: number;
  title: string | null;
  body: string | null;
};

export type ForegroundEventNotification = EventNotificationTarget & {
  title: string | null;
  body: string | null;
};

export type PushProvider = 'expo' | 'fcm';
export type PushPermissionState = 'granted' | 'denied' | 'undetermined' | 'unsupported';

export function pushProviderForPlatform(platform: string): PushProvider {
  return platform === 'android' ? 'fcm' : 'expo';
}

type NotificationData = Record<string, unknown> | null | undefined;

export function messageTargetFromData(data: NotificationData): MessageNotificationTarget | null {
  if (data?.type !== 'message') return null;
  const rawSenderId = data.senderId;
  const senderId = typeof rawSenderId === 'number'
    ? rawSenderId
    : typeof rawSenderId === 'string'
      ? Number(rawSenderId)
      : NaN;
  return Number.isInteger(senderId) && senderId > 0 ? { type: 'message', senderId } : null;
}

export function eventTargetFromData(data: NotificationData): EventNotificationTarget | null {
  if (data?.type !== 'notification_event') return null;
  const eventId = typeof data.eventId === 'string' ? data.eventId : '';
  const eventType = data.eventType;
  if (!/^[1-9]\d*$/.test(eventId)) return null;
  if (typeof eventType !== 'string' || !NOTIFICATION_EVENT_TYPES.has(eventType)) return null;
  if (data.destination === 'wallet' && eventType === 'escrow_expired_refunded') {
    return {
      type: 'notification_event',
      eventId,
      eventType: eventType as NotificationEventType,
      destination: 'wallet',
    };
  }
  if (data.destination === 'reservation') {
    const rawEntityId = data.entityId;
    const entityId = typeof rawEntityId === 'number' ? rawEntityId : Number(rawEntityId);
    if (!Number.isInteger(entityId) || entityId <= 0) return null;
    const actorId = typeof data.actorId === 'number' ? data.actorId : Number(data.actorId);
    if (!Number.isInteger(actorId) || actorId <= 0) return null;
    return {
      type: 'notification_event',
      eventId,
      eventType: eventType as NotificationEventType,
      destination: 'reservation',
      actorId,
      entityId,
    };
  }
  if (eventType === 'call_reservation_starting') {
    const rawEntityId = data.entityId;
    const entityId = typeof rawEntityId === 'number' ? rawEntityId : Number(rawEntityId);
    const actorId = typeof data.actorId === 'number' ? data.actorId : Number(data.actorId);
    if (!Number.isInteger(entityId) || entityId <= 0) return null;
    if (!Number.isInteger(actorId) || actorId <= 0) return null;
    return {
      type: 'notification_event',
      eventId,
      eventType: eventType as NotificationEventType,
      destination: 'reservation',
      actorId,
      entityId,
    };
  }
  const actorId = typeof data.actorId === 'number' ? data.actorId : Number(data.actorId);
  if (!Number.isInteger(actorId) || actorId <= 0) return null;
  const rawEntityId = data.entityId;
  const entityId = typeof rawEntityId === 'number' ? rawEntityId : Number(rawEntityId);
  return {
    type: 'notification_event',
    eventId,
    eventType: eventType as NotificationEventType,
    destination: 'conversation',
    actorId,
    ...(Number.isInteger(entityId) && entityId > 0 ? { entityId } : {}),
  };
}

export function shouldShowForegroundMessageBanner(
  currentScreen: string,
  openConversationId: number,
  senderId: number
): boolean {
  return currentScreen !== 'message' || openConversationId !== senderId;
}

function projectId(): string | null {
  return Constants.expoConfig?.extra?.eas?.projectId
    ?? Constants.easConfig?.projectId
    ?? null;
}

async function ensureAndroidChannel(selectedLanguage?: string): Promise<void> {
  if (Platform.OS !== 'android') return;
  const language = normalizeDisplayLanguage(selectedLanguage
    ?? await storage.getDisplayLanguage()
    ?? getDeviceLanguage());
  await Promise.all([
    { id: 'messages', key: 'notifications.messages', importance: Notifications.AndroidImportance.DEFAULT },
    { id: 'meetups', key: 'notifications.meetups', importance: Notifications.AndroidImportance.DEFAULT },
    { id: 'calls', key: 'notifications.calls', importance: Notifications.AndroidImportance.HIGH },
    { id: 'incoming-calls', key: 'notifications.incomingCalls', importance: Notifications.AndroidImportance.MAX },
    { id: 'financial', key: 'notifications.financial', importance: Notifications.AndroidImportance.DEFAULT },
    { id: 'reactions', key: 'notifications.reactions', importance: Notifications.AndroidImportance.DEFAULT },
  ].map(({ id, key, importance }) => Notifications.setNotificationChannelAsync(id, {
    name: translate(language, key),
    importance,
    sound: 'default',
  })));
}

let notificationHandlerConfigured = false;

/** Ön planda genel olayları uygulama içi banner gösterir; gelen arama ayrıca ses verir. */
export function configureForegroundNotificationPresentation(): void {
  if (notificationHandlerConfigured || Platform.OS === 'web') return;
  notificationHandlerConfigured = true;
  Notifications.setNotificationHandler({
    handleNotification: async (notification) => {
      const event = eventTargetFromData(notification.request.content.data);
      const incomingCall = event?.eventType === 'call_incoming';
      return {
        shouldShowBanner: false,
        shouldShowList: false,
        shouldPlaySound: incomingCall,
        shouldSetBadge: false,
      };
    },
  });
}

export function pushPermissionState(
  permission: Notifications.NotificationPermissionsStatus,
  platform = Platform.OS
): PushPermissionState {
  if (platform === 'web') return 'unsupported';
  const provisional = platform === 'ios'
    && permission.ios?.status === Notifications.IosAuthorizationStatus.PROVISIONAL;
  if (permission.status === 'granted' || permission.granted || provisional) return 'granted';
  if (permission.status === 'denied') return 'denied';
  return 'undetermined';
}

export async function getPushPermissionState(): Promise<PushPermissionState> {
  if (Platform.OS === 'web') return 'unsupported';
  try {
    return pushPermissionState(await Notifications.getPermissionsAsync());
  } catch {
    return 'unsupported';
  }
}

/** Yalnız açık bir kullanıcı eyleminden çağrılır; başarıda token'ı hemen eşler. */
export async function requestPushPermission(authToken: string): Promise<PushPermissionState> {
  if (!authToken || Platform.OS === 'web') return 'unsupported';
  try {
    await ensureAndroidChannel();
    const current = await Notifications.getPermissionsAsync();
    let state = pushPermissionState(current);
    if (state === 'granted') {
      await syncGrantedPushToken(authToken);
      return state;
    }
    // Reddedilmiş izin yeniden zorlanmaz; kullanıcı bundan sonra sistem Ayarları'nı kullanır.
    if (state === 'denied') return state;
    const requested = await Notifications.requestPermissionsAsync({
      ios: { allowAlert: true, allowBadge: true, allowSound: true },
    });
    state = pushPermissionState(requested);
    if (state === 'granted') await syncGrantedPushToken(authToken);
    return state;
  } catch (error) {
    console.warn('[notifications] bildirim izni alınamadı:', (error as Error).message);
    return getPushPermissionState();
  }
}

/** İzin zaten verilmişse token'ı kaydeder; kullanıcıya sistem istemi göstermez. */
export async function syncGrantedPushToken(
  authToken: string,
  devicePushToken?: Notifications.DevicePushToken,
  selectedLanguage?: string
): Promise<string | null> {
  if (!authToken || Platform.OS === 'web') return null;
  try {
    const language = normalizeDisplayLanguage(selectedLanguage
      ?? await storage.getDisplayLanguage()
      ?? getDeviceLanguage());
    await ensureAndroidChannel(language);
    const permission = await Notifications.getPermissionsAsync();
    if (pushPermissionState(permission) !== 'granted') return null;
    const provider = pushProviderForPlatform(Platform.OS);
    let pushToken: string;
    if (provider === 'fcm') {
      const nativeToken = devicePushToken ?? await Notifications.getDevicePushTokenAsync();
      if (typeof nativeToken.data !== 'string' || !nativeToken.data) return null;
      pushToken = nativeToken.data;
    } else {
      const easProjectId = projectId();
      if (!easProjectId) return null;
      pushToken = (await Notifications.getExpoPushTokenAsync({
        projectId: easProjectId,
        ...(devicePushToken ? { devicePushToken } : {}),
      })).data;
    }
    const result = await api.registerPushToken(
      authToken,
      pushToken,
      Platform.OS,
      provider,
      language,
      getDeviceTimeZone(),
      systemCallingAvailable
    );
    await storage.setRegisteredPushToken({
      token: pushToken,
      provider,
      deletionKey: result.deletionKey,
    });
    const pending = await storage.getPendingPushTokenDeletion();
    if (pending?.token === pushToken) await storage.clearPendingPushTokenDeletion();
    return pushToken;
  } catch (error) {
    // Bildirim yardımcı bir teslimat kanalıdır; token sorunu giriş akışını veya
    // mesajlaşmayı düşürmemeli. Sonraki açılış/yenilenme yeniden dener.
    console.warn('[notifications] push token eşlenemedi:', (error as Error).message);
    return null;
  }
}

/** Normal çıkışta sunucu eşlemesini kaldırır; ağ hatası çıkışı engellemez. */
export async function unregisterStoredPushToken(authToken: string): Promise<void> {
  const registration = await storage.getRegisteredPushToken();
  if (!registration) return;
  if (registration.deletionKey) await storage.setPendingPushTokenDeletion(registration);
  try {
    await api.deletePushToken(authToken || null, registration.token, registration.deletionKey);
    await storage.clearPendingPushTokenDeletion();
  } catch (error) {
    console.warn('[notifications] push token silinemedi:', (error as Error).message);
  } finally {
    // Çıkış yerelde her koşulda tamamlanır. Ağ hatasında yalnız tek-kayıtlık
    // silme işi bekler; kullanıcı JWT'si cihazda tutulmaz.
    await storage.clearRegisteredPushToken();
  }
}

/** Önceki çıkışta ağ kesildiyse, oturum gerektirmeyen tek-kayıt anahtarıyla tamamlar. */
export async function retryPendingPushTokenDeletion(): Promise<boolean> {
  const pending = await storage.getPendingPushTokenDeletion();
  if (!pending) return true;
  if (!pending.deletionKey) {
    await storage.clearPendingPushTokenDeletion();
    return false;
  }
  try {
    await api.deletePushToken(null, pending.token, pending.deletionKey);
    await storage.clearPendingPushTokenDeletion();
    return true;
  } catch (error) {
    console.warn('[notifications] bekleyen push token silinemedi:', (error as Error).message);
    return false;
  }
}

type ObserveOptions = {
  authToken: string;
  language: string;
  onMessageReceived: (notification: ForegroundMessageNotification) => void;
  onNotificationEventReceived?: (notification: ForegroundEventNotification) => void;
  onMessageOpened: (senderId: number) => void;
  onNotificationEventOpened?: (target: EventNotificationTarget) => void;
  onNotificationOpenFallback: () => void;
};

function openNotificationResponse(
  response: Notifications.NotificationResponse,
  options: ObserveOptions
): void {
  if (response.actionIdentifier !== Notifications.DEFAULT_ACTION_IDENTIFIER) return;
  const target = messageTargetFromData(response.notification.request.content.data);
  if (target) {
    options.onMessageOpened(target.senderId);
    return;
  }
  const eventTarget = eventTargetFromData(response.notification.request.content.data);
  if (eventTarget && options.onNotificationEventOpened) options.onNotificationEventOpened(eventTarget);
  else options.onNotificationOpenFallback();
}

/**
 * Bildirim dinleyicilerini oturum ömrüne bağlar. Soğuk başlangıç yanıtı işlenince
 * temizlenir; aksi halde sonraki oturum açılışında eski sohbet yeniden açılabilir.
 */
export function observePushLifecycle(options: ObserveOptions): () => void {
  if (Platform.OS === 'web' || !options.authToken) return () => {};
  configureForegroundNotificationPresentation();
  let active = true;

  syncGrantedPushToken(options.authToken, undefined, options.language).catch(() => {});

  const tokenSubscription = Notifications.addPushTokenListener((deviceToken) => {
    if (active) syncGrantedPushToken(options.authToken, deviceToken, options.language).catch(() => {});
  });
  const receivedSubscription = Notifications.addNotificationReceivedListener((notification) => {
    const target = messageTargetFromData(notification.request.content.data);
    if (active && target) {
      const { title, body } = notification.request.content;
      options.onMessageReceived({
        senderId: target.senderId,
        title: typeof title === 'string' && title.trim() ? title.trim() : null,
        body: typeof body === 'string' && body.trim() ? body.trim() : null,
      });
      return;
    }
    const eventTarget = eventTargetFromData(notification.request.content.data);
    if (active && eventTarget) {
      const { title, body } = notification.request.content;
      options.onNotificationEventReceived?.({
        ...eventTarget,
        title: typeof title === 'string' && title.trim() ? title.trim() : null,
        body: typeof body === 'string' && body.trim() ? body.trim() : null,
      });
    }
  });
  const responseSubscription = Notifications.addNotificationResponseReceivedListener((response) => {
    if (active) openNotificationResponse(response, options);
    Notifications.clearLastNotificationResponse();
  });

  try {
    const initialResponse = Notifications.getLastNotificationResponse();
    if (initialResponse) openNotificationResponse(initialResponse, options);
    if (initialResponse) Notifications.clearLastNotificationResponse();
  } catch {
    // Eski/sınırlı native build modülü sağlamıyorsa uygulama açılışı sürsün.
  }

  return () => {
    active = false;
    tokenSubscription.remove();
    receivedSubscription.remove();
    responseSubscription.remove();
  };
}
